import React from 'react';
import { styled } from '@compiled/react';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { ListFilter, type CheckboxSelectEntry, type Options } from '@atlassian/jira-filters';
import { useIntl } from '@atlassian/jira-intl';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { DeploymentEnvironmentType as EnvironmentType } from '../../../common/types';
import messages from './messages';
import { useSelectState } from './utils';

const MAX_ENVIRONMENT_NAMES = 50;

const useFormatEnvironmentType = () => {
	const { formatMessage } = useIntl();

	const labels = {
		PRODUCTION: formatMessage(messages.production),
		STAGING: formatMessage(messages.staging),
		TESTING: formatMessage(messages.testing),
		DEVELOPMENT: formatMessage(messages.development),
		UNMAPPED: formatMessage(messages.unmapped),
	};

	return (type: EnvironmentType): string => labels[type];
};

// this component will be removed during ff cleanup - a11y-jira-team.deployment-filter-and-table_m845b
export const EnvironmentFilter = () => {
	const format = useFormatEnvironmentType();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { entries, selectedEntryIds, onChange } = useSelectState(format);
	const { formatMessage } = useIntl();

	return (
		<FilterContainer>
			<ListFilter
				label={formatMessage(messages.environmentLabel)}
				values={entries}
				selectedValues={selectedEntryIds}
				onChange={(newEntries: CheckboxSelectEntry[]) => {
					const filteringEnvNamesByEnvType = newEntries.some((entry) =>
						entry.value.includes('environment-type'),
					);
					const filteringBySpecificEnvName = newEntries.some((entry) =>
						entry.value.includes('environment-name'),
					);
					fireUIAnalytics(
						createAnalyticsEvent({
							action: 'toggled',
							actionSubject: 'deploymentsEnvironmentNameFilter',
						}),
						{
							filteringEnvNamesByEnvType,
							filteringBySpecificEnvName,
						},
					);
					onChange(newEntries);
				}}
				maxSelectionAllowed={MAX_ENVIRONMENT_NAMES}
				maxSelectionReachedTooltip={formatMessage(messages.maxEnvironmentNamesTooltip)}
			/>
		</FilterContainer>
	);
};

type Props = {
	isNested?: boolean;
	onChange: (arg: CheckboxSelectEntry[]) => void;
	options: Options;
	selectedValues: string[];
};

export const EnvironmentFilterNew = ({ isNested, onChange, options, selectedValues }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const { formatMessage } = useIntl();

	return (
		<FilterContainer>
			<ListFilter
				label={formatMessage(messages.environmentLabel)}
				values={options}
				selectedValues={selectedValues}
				onChange={(newEntries: CheckboxSelectEntry[]) => {
					const filteringEnvNamesByEnvType = newEntries.some((entry) =>
						entry.value.includes('environment-type'),
					);
					const filteringBySpecificEnvName = newEntries.some((entry) =>
						entry.value.includes('environment-name'),
					);
					fireUIAnalytics(
						createAnalyticsEvent({
							action: 'toggled',
							actionSubject: 'deploymentsEnvironmentNameFilter',
						}),
						{
							filteringEnvNamesByEnvType,
							filteringBySpecificEnvName,
						},
					);
					onChange(newEntries);
				}}
				maxSelectionAllowed={MAX_ENVIRONMENT_NAMES}
				maxSelectionReachedTooltip={formatMessage(messages.maxEnvironmentNamesTooltip)}
				isNested={isNested}
			/>
		</FilterContainer>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const FilterContainer = styled.div({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values -- Ignored via go/DSP-18766
	backgroundColor: token('color.background.input', colors.N0),
});

export default EnvironmentFilter;
export { useSelectState, useFormatEnvironmentType } from './utils';
